const DISPLAY_SCORE_DECIMALS = 3
const DISPLAY_NUMBER_DECIMALS = 2

/**
 * スコアを表示用の文字列にする。
 *
 * 以前は文字列を先頭から切り出していたため、0.0009 が "0.000" になって 0 と
 * 区別がつかず、指数表記になる 5e-7 は "5e-70" と壊れて表示されていた。
 * 四捨五入で丸めたうえで、丸めると 0 になってしまう小さな値だけは
 * 指数表記にして 0 と見分けられるようにする。
 */
export const getShortenedScore = (score: number): string => {
  if (!Number.isFinite(score)) return ""
  const rounded = score.toFixed(DISPLAY_SCORE_DECIMALS)
  if (score !== 0 && Number(rounded) === 0) {
    return score.toExponential(1)
  }
  return rounded
}

export const getShortenedTimeAndMemory = (n: number): string => {
  if (!Number.isFinite(n)) return ""
  return n.toFixed(DISPLAY_NUMBER_DECIMALS)
}

const UPLOAD_ERROR_FALLBACK = "アップロードに失敗しました。"

/**
 * 予測結果（CSV / JSON）提出APIのエラーからトーストに出す文言を取り出す。
 *
 * 提出APIはフォーマット不正の理由を日本語の detail にして 400 で返すが、
 * これまでは「アップロードに失敗しました。」の固定文言に潰していたため、
 * 参加者は何を直せばよいか分からないままだった。
 * 一方で backend は 401 / 403 / 429 / 5xx の内部文言（英語の例外メッセージなど）も
 * 同じ detail に載せるので、表示するのは 400 のときだけにする。
 *
 * langflow / program の提出APIは 400 にも GCS の例外全文などの内部文言を載せるため、
 * この関数は使わず固定文言にしている。
 */
export const getUploadErrorMessage = (e: unknown): string => {
  const response = (e as { response?: { status?: unknown; data?: { detail?: unknown } } })
    ?.response
  if (response?.status !== 400) return UPLOAD_ERROR_FALLBACK
  const detail = response.data?.detail
  return typeof detail === "string" && detail.trim() !== "" ? detail : UPLOAD_ERROR_FALLBACK
}

/**
 * 「3分前」「2時間前」のような経過時間。Kaggle のリーダーボードの Last 列に当たる。
 * 1日以上前は日付と時刻にする（終了したコンペを後から見るとき、相対表示は役に立たないため）。
 */
export const formatTimeAgo = (
  value: string | Date | null | undefined,
  now: Date = new Date()
): string => {
  if (!value) return "";
  const t = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(t.getTime())) return "";
  const diffSec = Math.max(0, Math.floor((now.getTime() - t.getTime()) / 1000));
  if (diffSec < 60) return "たった今";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}分前`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}時間前`;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${t.getMonth() + 1}/${t.getDate()} ${pad(t.getHours())}:${pad(t.getMinutes())}`;
};
